import { ArrowDownward, ArrowUpward, TrendingUp, TrendingDown, Sync } from '@mui/icons-material';
import { Avatar, Button, Card, Chip, Snackbar, Typography, Stack, Table } from '@mui/joy';
import { Report } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/authContext';
import LoadingSpinnerComponent from '../components/LoadingSpinnerComponent';

const networking = require('../Networking/Benchmarks/benchmarkAPI');
const globalNetworking = require('../Networking/API');
const helper = require('../Helpers/helpers');

function BenchmarkPage({ subscription, app }: any) {
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);
    const [data, setData] = useState([]);
    const [sentimentScore, setSentimentScore] = useState('0');
    const [lastSyncDate, setLastSyncDate] = useState<string | null>(null);

    const hasFetched = useRef(false);

    useEffect(() => {
        if (!hasFetched.current) {
            getBenchmarkData();
            hasFetched.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function getBenchmarkData() {
        setLoading(true);
        networking.getAppBenchmarkList(app.id).then((data: any) => {
            getReviews(data);
        }).catch((error: Error) => {
            setError(error.message);
            setLoading(false);
        });
    }

    function getReviews(data: any) {
        globalNetworking.reviewsFor(app.id, token).then((reviews: any) => {
            const badReviews = reviews.filter((d: any) => d.sentiment === 'bad').length;
            const goodReviews = reviews.filter((d: any) => d.sentiment === 'good').length;

            const total = reviews.length;

            const badPercentage = badReviews / total;
            const goodPercentage = goodReviews / total;

            const sentimentValue = (goodPercentage * 100 - badPercentage * 100) / 100;

            // If you want to represent it as a percentage score:
            const sentimentPercentage = (sentimentValue + 1) * 50;
            setSentimentScore(sentimentPercentage.toFixed(0));

            setData(data.data.benchmark);
            // Set last sync date to current date (update this when API provides actual sync date)
            setLastSyncDate(new Date().toISOString());
            setLoading(false);
        }).catch((error: Error) => {
            setError(error.message);
            setLoading(false);
        });
    }

    return (
        <div>
            {showError &&
                <Snackbar
                    variant="soft"
                    color="danger"
                    open={showError}
                    onClose={() => setShowError(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowError(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {error}
                </Snackbar>
            }

            {lastSyncDate && (
                <Typography level="body-xs" sx={{ mt: 1, color: '#666', mb: 2 }}>
                    <Sync sx={{ width: '14px', height: '14px', mr: 0.5, verticalAlign: 'middle' }} />
                    Last synced: {helper.timeSince(lastSyncDate)}
                </Typography>
            )}

            {loading ?
                <LoadingSpinnerComponent />
                :
                <>
                    {data &&
                        <>
                            {/* App Store Benchmarks */}
                            {app && app.appleappid &&
                                <div className='mb-4'>
                                    <BenchmarkStoreGrid
                                        userApp={app}
                                        benchmarks={data.filter((b: any) => b.store === 'applestore')}
                                        userScore={sentimentScore}
                                        store="applestore"
                                        userRatingCount={app.apple_rating_count}
                                        userRatingValue={app.apple_rating_value}
                                        positionInCategory={app.position_in_category}
                                    />
                                </div>
                            }

                            {/* Play Store Benchmarks */}
                            {app && app.androidpackageid &&
                                <div className='mb-4'>
                                    <BenchmarkStoreGrid
                                        userApp={app}
                                        benchmarks={data.filter((b: any) => b.store === 'playstore')}
                                        userScore={sentimentScore}
                                        store="playstore"
                                        userRatingCount={app.google_rating_count}
                                        userRatingValue={app.google_rating_value}
                                    />
                                </div>
                            }
                        </>
                    }
                </>
            }
        </div>
    );
}

// Helper function to calculate percentage change
const calculatePercentageChange = (current: number, previous: number): number => {
    if (previous === 0 || previous === undefined) return 0;
    return Math.round(((current - previous) / Math.abs(previous)) * 100 * 10) / 10; // Round to 1 decimal place
};

// Benchmark Grid Component - Table Layout
const BenchmarkStoreGrid = ({ userApp, benchmarks, userScore, userRatingCount, userRatingValue, store, positionInCategory }: any) => {
    // Combine user app with benchmarks
    const allApps = [
        {
            app: userApp,
            isUserApp: true,
            ratingCount: userRatingCount,
            ratingValue: userRatingValue,
            sentimentScore: userScore,
            matchScore: 100,
            positionInCategory: positionInCategory || '-'
        },
        ...benchmarks.map((benchmark: any) => ({
            app: benchmark,
            isUserApp: false,
            ratingCount: benchmark.ratings_count,
            ratingValue: benchmark.avg_rating,
            sentimentScore: benchmark.sentiment_score,
            previousSentimentScore: benchmark.previous_sentiment_score,
            matchScore: benchmark.match_score || Math.floor(Math.random() * 100),
            positionInCategory: benchmark.position_in_category || '-'
        }))
    ];

    const metrics = [
        { key: 'name', label: '' },
        { key: 'ratings', label: '# Ratings' },
        { key: 'avgRating', label: 'Avg. Rating' },
        { key: 'sentiment', label: 'Sentiment Score' },
        { key: 'sentimentTrend', label: 'Sentiment Trend' },
        { key: 'positionInCategory', label: 'Position' }

    ];

    const getMetricValue = (appData: any, metricKey: string) => {
        switch (metricKey) {
            case 'name':
                return appData.app.name;
            case 'ratings':
                return appData.ratingCount;
            case 'avgRating':
                return typeof appData.ratingValue === 'number' ? appData.ratingValue.toFixed(2) : appData.ratingValue;
            case 'sentiment':
                return appData.sentimentScore;
            case 'sentimentTrend':
                // Convert numeric sentiment_trend to trend direction
                const trendValue = appData.app.sentiment_trend;
                if (typeof trendValue === 'number') {
                    return trendValue > 0 ? 'up' : trendValue < 0 ? 'down' : 'stable';
                }
                return trendValue || 'stable'; // Fallback if already a string
            case 'positionInCategory':
                return appData.positionInCategory || '-';
            default:
                return '-';
        }
    };

    const getComparisonArrow = (userValue: any, competitorValue: any, isNumeric: boolean = true) => {
        if (!isNumeric) return null;
        const userNum = Number(userValue);
        const compNum = Number(competitorValue);
        if (userNum > compNum) return <ArrowUpward sx={{ width: '14px' }} color="success" />;
        if (userNum < compNum) return <ArrowDownward sx={{ width: '14px' }} color="error" />;
        return null;
    };

    return (
        <Card variant='plain' sx={{ padding: '0px' }}>
            <Table sx={{ border: '1px solid #e5e7eb' }}>
                <thead>
                    <tr>
                        <th
                            style={{
                                width: '140px',
                                backgroundColor: '#f9f9f9',
                                padding: '12px',
                                textAlign: 'center',
                                fontWeight: 700,
                                borderRight: '1px solid #e5e7eb'
                            }}>
                            <Stack direction="column" spacing={1} sx={{ alignItems: 'center' }}>
                                <Avatar>
                                    {helper.iconFor(store)}
                                </Avatar>
                            </Stack>
                        </th>
                        {allApps.map((appData: any, idx: number) => (
                            <th
                                key={idx}
                                style={{
                                    minWidth: '180px',
                                    backgroundColor: appData.isUserApp ? '#e8f4f8' : '#f9f9f9',
                                    padding: '12px',
                                    textAlign: 'center',
                                    fontWeight: 700,
                                    borderRight: '1px solid #e5e7eb'
                                }}
                            >
                                <Stack direction="column" spacing={1} sx={{ alignItems: 'center' }}>
                                    <Avatar
                                        alt={appData.app.name}
                                        src={appData.app.icon}
                                        sx={{ width: 40, height: 40, borderRadius: '20%' }}
                                    >
                                        {appData.app.name?.charAt(0).toUpperCase()}
                                    </Avatar>
                                </Stack>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {metrics.map((metric: any, metricIdx: number) => (
                        <tr key={metricIdx}>
                            <td style={{ backgroundColor: '#f9f9f9', padding: '12px', fontWeight: 500 }}>
                                <Typography level="body-xs" sx={{ fontWeight: 500 }}>
                                    {metric.label}
                                </Typography>
                            </td>
                            {allApps.map((appData: any, appIdx: number) => {
                                const value = getMetricValue(appData, metric.key);
                                const userValue = appIdx === 0 ? value : getMetricValue(allApps[0], metric.key);
                                const isNumeric = metric.key !== 'sentimentTrend';

                                return (
                                    <td
                                        key={appIdx}
                                        style={{
                                            minWidth: '180px',
                                            backgroundColor: appData.isUserApp ? '#e8f4f8' : 'white',
                                            padding: '12px',
                                            textAlign: 'center',
                                            borderRight: '1px solid #e5e7eb'
                                        }}
                                    >
                                        {metric.key === 'sentimentTrend' ? (
                                            <SentimentTrendCell
                                                trend={value}
                                                sentimentScore={appData.sentimentScore}
                                                previousSentimentScore={appData.previousSentimentScore}
                                            />
                                        ) : metric.key === 'matchScore' ? (
                                            <MatchScoreCell matchScore={value} />
                                        ) : appData.isUserApp ? (
                                            <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                                                {value}
                                            </Typography>
                                        ) : metric.key === 'name' ? (
                                            <Typography level="body-xs" sx={{ fontWeight: 600, fontSize: '10px' }}>
                                                {appData.app.name}
                                            </Typography>
                                        ) : metric.key === 'positionInCategory' && value !== '-' ? (
                                            <>
                                                <Chip
                                                    size='sm'
                                                    variant="soft"
                                                    color="primary"
                                                >
                                                    #{value}
                                                </Chip>
                                                {isNumeric && getComparisonArrow(userValue, value, isNumeric) && (
                                                    <>
                                                        {getComparisonArrow(userValue, value, isNumeric)}
                                                    </>
                                                )}
                                            </>
                                        )
                                            : (
                                                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                                                    <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                                                        {value}
                                                    </Typography>
                                                    {isNumeric && getComparisonArrow(userValue, value, isNumeric) && (
                                                        <>
                                                            {getComparisonArrow(userValue, value, isNumeric)}
                                                        </>
                                                    )}
                                                </Stack>
                                            )
                                        }
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Card>
    );
};

// Sentiment Trend Cell Component
const SentimentTrendCell = ({ trend, sentimentScore, previousSentimentScore }: any) => {
    const percentChange = calculatePercentageChange(sentimentScore, previousSentimentScore);

    const getTrendIcon = () => {
        switch (trend) {
            case 'up':
                return <TrendingUp sx={{ width: '16px', color: '#34A853' }} />;
            case 'down':
                return <TrendingDown sx={{ width: '16px', color: '#EA4335' }} />;
            default:
                return null;
        }
    };

    const getTrendColor = () => {
        return trend === 'up' ? 'success' : trend === 'down' ? 'danger' : 'neutral';
    };

    return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
            {trend !== 'stable' ? (
                <>
                    {getTrendIcon()}
                    < Chip
                        size='sm'
                        variant="soft"
                        color={getTrendColor()}
                    >
                        {(percentChange !== 0 && percentChange !== undefined && percentChange !== -Infinity) && `${percentChange > 0 ? '+' : ''}${percentChange}%`}
                    </Chip>
                </>
            ) : '-'}
        </Stack >
    );
};

// Match Score Cell Component
const MatchScoreCell = ({ matchScore }: any) => {
    const getScoreColor = () => {
        if (matchScore >= 80) return 'success';
        if (matchScore >= 60) return 'warning';
        return 'danger';
    };

    return (
        <Chip
            size='sm'
            variant="soft"
            color={getScoreColor()}
        >
            {`${matchScore}%`}
        </Chip>
    );
};

export default BenchmarkPage;
